/*
LIME LABS COMMERCE LAYER
Component: PLP Mobile Scroll Reveal
Project: La Troupe
File: assets/ll-collection-mobile-reveal.js
Version: 1.0
Status: LLCL-QA

Purpose
- Replaces desktop hover semantics with a scroll-position cue on touch/mobile PLPs.
- When the bottom edge of a product image reaches a narrow band around 60% of the viewport,
  that card becomes the active card and its color/size utility rows are revealed.
- Keeps only one active card at a time and fails open if IntersectionObserver is unavailable.
*/

(() => {
  'use strict';

  if (window.__LLCL_COLLECTION_MOBILE_REVEAL_V1__) return;
  window.__LLCL_COLLECTION_MOBILE_REVEAL_V1__ = true;

  const MOBILE_QUERY = '(max-width: 749px)';
  const ROOT_SELECTOR = '.shopify-section--main-collection';
  const CARD_SELECTOR = '.product-card';
  const FIGURE_SELECTOR = '.product-card__figure';
  const SENTINEL_CLASS = 'll-mobile-reveal-sentinel';
  const ACTIVE_CLASS = 'is-ll-scroll-active';
  const ROOT_CLASS = 'll-mobile-reveal-enabled';

  const mobile = window.matchMedia(MOBILE_QUERY);

  let observer = null;
  let mutationObserver = null;
  let root = null;
  let activeCard = null;
  let refreshFrame = 0;

  const getCards = () => root ? Array.from(root.querySelectorAll(CARD_SELECTOR)) : [];

  const clearActive = () => {
    if (activeCard) activeCard.classList.remove(ACTIVE_CLASS);
    activeCard = null;
  };

  const setActive = (card) => {
    if (!card || card === activeCard) return;

    if (activeCard) activeCard.classList.remove(ACTIVE_CLASS);
    activeCard = card;
    activeCard.classList.add(ACTIVE_CLASS);
  };

  const createSentinel = (card) => {
    const figure = card.querySelector(FIGURE_SELECTOR);
    if (!figure) return null;

    let sentinel = figure.querySelector(`.${SENTINEL_CLASS}`);

    if (!sentinel) {
      sentinel = document.createElement('span');
      sentinel.className = SENTINEL_CLASS;
      sentinel.setAttribute('aria-hidden', 'true');
      figure.appendChild(sentinel);
    }

    return sentinel;
  };

  const chooseClosestCard = () => {
    if (!mobile.matches || !root || activeCard) return;

    const targetY = window.innerHeight * 0.60;
    let bestCard = null;
    let bestDistance = Infinity;

    getCards().forEach((card) => {
      const figure = card.querySelector(FIGURE_SELECTOR);
      if (!figure) return;

      const rect = figure.getBoundingClientRect();
      const distance = Math.abs(rect.bottom - targetY);

      if (rect.bottom >= 0 && rect.top <= window.innerHeight && distance < bestDistance) {
        bestDistance = distance;
        bestCard = card;
      }
    });

    if (bestCard) setActive(bestCard);
  };

  const onIntersection = (entries) => {
    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => Math.abs(a.boundingClientRect.top - window.innerHeight * 0.60) - Math.abs(b.boundingClientRect.top - window.innerHeight * 0.60));

    if (!visible.length) return;

    const card = visible[0].target.closest(CARD_SELECTOR);
    if (card) setActive(card);
  };

  const buildObserver = () => {
    if (!('IntersectionObserver' in window)) return false;

    if (observer) observer.disconnect();

    observer = new IntersectionObserver(onIntersection, {
      root: null,
      rootMargin: '-58% 0px -38% 0px',
      threshold: 0
    });

    getCards().forEach((card) => {
      const sentinel = createSentinel(card);
      if (sentinel) observer.observe(sentinel);
    });

    return true;
  };

  const refresh = () => {
    refreshFrame = 0;

    if (!mobile.matches || !root || !observer) return;

    observer.disconnect();

    getCards().forEach((card) => {
      const sentinel = createSentinel(card);
      if (sentinel) observer.observe(sentinel);
    });

    if (activeCard && !document.documentElement.contains(activeCard)) {
      activeCard = null;
    }

    requestAnimationFrame(chooseClosestCard);
  };

  const scheduleRefresh = () => {
    if (refreshFrame) return;
    refreshFrame = requestAnimationFrame(refresh);
  };

  const startMutationObserver = () => {
    if (!root || !('MutationObserver' in window)) return;

    if (mutationObserver) mutationObserver.disconnect();

    mutationObserver = new MutationObserver((mutations) => {
      const hasProductChange = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches?.(CARD_SELECTOR) || node.querySelector?.(CARD_SELECTOR);
        });
      });

      if (hasProductChange) scheduleRefresh();
    });

    mutationObserver.observe(root, {
      childList: true,
      subtree: true
    });
  };

  const disable = () => {
    document.documentElement.classList.remove(ROOT_CLASS);

    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }

    if (refreshFrame) {
      cancelAnimationFrame(refreshFrame);
      refreshFrame = 0;
    }

    clearActive();
  };

  const enable = () => {
    root = document.querySelector(ROOT_SELECTOR);

    if (!root || !mobile.matches || !('IntersectionObserver' in window)) {
      disable();
      return;
    }

    const ready = buildObserver();

    if (!ready) {
      disable();
      return;
    }

    document.documentElement.classList.add(ROOT_CLASS);
    startMutationObserver();

    requestAnimationFrame(() => {
      chooseClosestCard();
    });
  };

  const sync = () => {
    if (mobile.matches) enable();
    else disable();
  };

  const onSectionLoad = (event) => {
    if (!event?.target) return;
    if (event.target.matches?.(ROOT_SELECTOR) || event.target.querySelector?.(ROOT_SELECTOR)) {
      disable();
      requestAnimationFrame(enable);
    }
  };

  if (typeof mobile.addEventListener === 'function') {
    mobile.addEventListener('change', sync);
  } else if (typeof mobile.addListener === 'function') {
    mobile.addListener(sync);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:reorder', scheduleRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }
})();
